import { ReactNode, useMemo, useState } from 'react';
import { MdArrowForward, MdChevronRight, MdExpandLess, MdExpandMore } from 'react-icons/md';
import cn from 'classnames';
import { Button } from '@Components/Button/Button';
import { InfoTooltip } from '@Components/InfoTooltip';
import { IconButton } from '@Components/IconButton';
import { InteractiveSpan, InteractiveTr } from '@Components/InteractiveEl';
import { Appearance, Size } from '@Components/designConstants';
import {
  BpmComparison,
  buildChannelSummaryRows,
  ChannelSummaryRow,
  ModelOutcomeRow,
  ModelReviewProgress,
  ModelTarget,
  PlatformSummaryRow
} from '../about-summary-utils';
import { GROUP_SHORT_LABEL } from '../data-qa-constants';
import { DataQaDataset, MmmConfig } from '../data-qa-types';
import { filterWeeksToFencepost, modelTypeForProductType } from '../data-qa-utils';
import {
  buildChannelViews,
  ChannelView,
  reasonsIndicator,
  reasonTexts
} from '../media-inputs-utils';
import { IndicatorDot } from '../components/IndicatorDot';
import { ModelCapsule } from '../components/ModelCapsule';
import { InformativePriors } from '../components/InformativePriors';
import { PriorsStage, PriorsStageTabs } from '../components/PriorsStageTabs';
import { SectionHeader } from '../components/SectionHeader';
import { StepKey, StepKeyType } from '../components/SectionStepper';
import { FeatureMappingFocus } from './FeatureMappingSection';
import { buildFeatureSummaryRows, FeatureSummaryRow } from './feature-mapping-summary-utils';

const integerFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const averageFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero'
});

interface AboutSectionProps {
  config: MmmConfig;
  dataset: DataQaDataset;
  // Brand Equity's second stage's own priors (e.g. Total Business's single organic
  // prior on GQV, alongside GQV's own paid-media priors) — shown as a second tab
  // alongside the primary stage's. Undefined for every other product type, which has
  // just one set of priors and renders them plainly.
  secondaryPriorsStage?: PriorsStage;
  // Every model for the company, for the Outcome KPI table (see buildModelOutcomeRows).
  models: ModelOutcomeRow[];
  onOpenStep: (step: StepKeyType) => void;
  onOpenChannel: (channelKey: string) => void;
  onOpenFeature: (focus: FeatureMappingFocus) => void;
  onSelectModel: (configPath: string) => void;
}

// What each review tab covers, in stepper order, for the "How to review" popover.
const TOOL_STEPS: [string, string][] = [
  ['Campaigns', 'campaign-taxonomy labeling and delivery coverage'],
  [
    'Media Inputs',
    'total and per-channel spend and impressions vs. the canonical Bliss Point database'
  ],
  ['Feature Mapping', 'how raw data flows into MMM features'],
  ['Context Variables', 'non-marketing model inputs (if configured)'],
  ['Dependent Variable', 'the target variable'],
  ['Summary', 'review status across models, and export']
];

const FEATURES_TOOLTIP =
  'Defined = paid-media features declared in the config. Mapped = how many appear ' +
  'in the mapping data, meaning a row links them to source delivery. A ' +
  'defined-but-unmapped feature receives no data and must be mapped or removed ' +
  'before training.';

// dataset.reviewGranularity is the model's own declared review cadence (can differ
// from dataset.sourceGranularity, the raw data frame's — see data-qa-types.ts). This
// labels the unit for dataset.dataPointCount, not `weeks.length`.
const GRANULARITY_UNIT_LABELS: Record<string, string> = {
  daily: 'Days',
  weekly: 'Weeks',
  monthly: 'Months'
};

const dataPointsLabel = (granularity?: string): string => {
  const unit = granularity ? GRANULARITY_UNIT_LABELS[granularity] : undefined;
  return unit ? `Data Points (${unit})` : 'Data Points';
};

// The model at a glance, before any review: its setup in one row, then the channel totals
// against BPM, the Demand Generation / Demand Capture split, and every model's outcome KPI.
// Each card links to the tab that reviews it, and its rows open that tab on the row.
export const AboutSection = ({
  config,
  dataset,
  secondaryPriorsStage,
  models,
  onOpenStep,
  onOpenChannel,
  onOpenFeature,
  onSelectModel
}: AboutSectionProps) => {
  const { priors } = dataset;
  const modelType = modelTypeForProductType(config.productType);

  return (
    <div className='data-qa-about'>
      <SectionHeader
        title='About'
        modelType={modelType}
        subtitle="This model's inputs at a glance. Select a row to open it on its tab."
        tips={
          <>
            <li>
              A Media Mix Model attributes a business outcome to marketing spend, so it&apos;s only
              as trustworthy as its data. This tool is a guided check of that data <em>before</em>{' '}
              the model is trained: approve or flag each tab as you go.
            </li>
            {TOOL_STEPS.map(([name, desc]) => (
              <li key={name}>
                <strong>{name}</strong>: {desc}
              </li>
            ))}
            <li>
              <strong>Variance chips</strong>: MMM vs. BPM over the modeling range — green within
              0.5%, yellow 0.5–1%, red over 1%. The dot beside each channel is its Media Inputs
              indicator, which also checks week by week; hover it for why. Expand a channel to see
              the same by platform.
            </li>
          </>
        }
      />

      <ModelStats config={config} dataset={dataset} secondaryPriorsStage={secondaryPriorsStage} />

      <SummaryCard
        title='Channel-level data'
        linkLabel='Open Media Inputs'
        onOpen={() => onOpenStep(StepKey.MEDIA_INPUTS)}
      >
        <ChannelTable config={config} dataset={dataset} onOpenChannel={onOpenChannel} />
      </SummaryCard>

      <SummaryCard
        title='Demand Generation vs. Demand Capture'
        linkLabel='Open Feature Mapping'
        onOpen={() => onOpenStep(StepKey.FEATURE_MAPPING)}
      >
        <DemandGroups config={config} onOpenFeature={onOpenFeature} />
      </SummaryCard>

      <SummaryCard
        title='Outcome KPI'
        linkLabel='Open Dependent Variable'
        onOpen={() => onOpenStep(StepKey.DEPENDENT_VAR)}
      >
        <OutcomeTable models={models} onSelectModel={onSelectModel} />
      </SummaryCard>

      {secondaryPriorsStage ? (
        <PriorsStageTabs
          stages={[{ label: config.dependentVarLabel, priors: priors ?? [] }, secondaryPriorsStage]}
        />
      ) : (
        <InformativePriors priors={priors ?? []} />
      )}
    </div>
  );
};

interface SummaryCardProps {
  title: string;
  linkLabel: string;
  onOpen: () => void;
  children: ReactNode;
}

// A collapsible summary card, toggled from the upper right like the other Data QA cards,
// with a link to the tab that reviews it beside its title. Starts open.
const SummaryCard = ({ title, linkLabel, onOpen, children }: SummaryCardProps) => {
  const [open, setOpen] = useState(true);

  return (
    <section className='data-qa-about__card'>
      <div
        className={cn('data-qa-about__card-head', { 'data-qa-about__card-head--closed': !open })}
      >
        <div className='data-qa-about__card-heading'>
          <h2 className='data-qa-about__card-title'>{title}</h2>
          <Button
            size={Size.SM}
            appearance={Appearance.EMPTY}
            trailingElement={<MdArrowForward />}
            onClick={onOpen}
          >
            {linkLabel}
          </Button>
        </div>
        <IconButton
          appearance={Appearance.EMPTY}
          aria-expanded={open}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          onClick={() => setOpen(o => !o)}
        >
          {open ? <MdExpandLess /> : <MdExpandMore />}
        </IconButton>
      </div>
      {open && children}
    </section>
  );
};

interface ModelStatsProps {
  config: MmmConfig;
  dataset: DataQaDataset;
  secondaryPriorsStage?: PriorsStage;
}

const ModelStats = ({ config, dataset, secondaryPriorsStage }: ModelStatsProps) => {
  // The model's own training window, not the raw data frame's full history -- a
  // Commerce model trained on 13 months of data shouldn't read as "5.5 years of data"
  // just because its underlying dataframe happens to span that far back (Sean,
  // 2026-09-09). Same fencepost-scoping every other section applies to its own charts.
  const modelingWeeks = filterWeeksToFencepost(
    dataset.weeks,
    config.fencepostStart,
    config.fencepostEnd,
    dataset.reviewGranularity
  );
  const dateMin = modelingWeeks.length ? modelingWeeks[0].weekIso : '—';
  const dateMax = modelingWeeks.length ? modelingWeeks[modelingWeeks.length - 1].weekIso : '—';
  const channelNames = config.channels.map(c => c.label).join(', ') || 'No channels detected';
  const { definedFeatureCount, mappedFeatureCount } = config;
  const allMapped =
    mappedFeatureCount === undefined ||
    definedFeatureCount === undefined ||
    mappedFeatureCount >= definedFeatureCount;

  return (
    <div className='data-qa-about__card data-qa-about__stats'>
      <div className='data-qa-about__stat'>
        <div className='data-qa-about__stat-label'>Modeling Range</div>
        <div className='data-qa-about__stat-value data-qa-about__stat-value--sm'>
          {dateMin}
          <br />
          to {dateMax}
        </div>
      </div>

      <div className='data-qa-about__stat'>
        <div className='data-qa-about__stat-label'>
          {dataPointsLabel(dataset.reviewGranularity)}
        </div>
        <div className='data-qa-about__stat-value'>
          {integerFormatter.format(dataset.dataPointCount)}
        </div>
        <div className='data-qa-about__stat-sub'>
          Original: {dataset.sourceGranularity ?? 'unknown'} data
        </div>
      </div>

      <div className='data-qa-about__stat'>
        <div className='data-qa-about__stat-label'>
          Channels
          <InfoTooltip>{channelNames}</InfoTooltip>
        </div>
        <div className='data-qa-about__stat-value'>{config.channels.length}</div>
      </div>

      <div className='data-qa-about__stat'>
        <div className='data-qa-about__stat-label'>
          Model Features
          <InfoTooltip>{FEATURES_TOOLTIP}</InfoTooltip>
        </div>
        {definedFeatureCount === undefined ? (
          <div className='data-qa-about__stat-value'>—</div>
        ) : (
          <>
            <div className='data-qa-about__stat-value'>{definedFeatureCount} defined</div>
            {mappedFeatureCount !== undefined && (
              <div
                className={cn('data-qa-about__stat-mapped', {
                  'data-qa-about__stat-mapped--ok': allMapped,
                  'data-qa-about__stat-mapped--warn': !allMapped
                })}
              >
                {mappedFeatureCount} mapped
              </div>
            )}
          </>
        )}
      </div>

      <div className='data-qa-about__stat'>
        <div className='data-qa-about__stat-label'>Target Variable(s)</div>
        <div className='data-qa-about__stat-value data-qa-about__stat-value--sm'>
          {secondaryPriorsStage
            ? `${config.dependentVarLabel}, ${secondaryPriorsStage.label}`
            : config.dependentVarLabel}
        </div>
      </div>
    </div>
  );
};

interface ChannelTableProps {
  config: MmmConfig;
  dataset: DataQaDataset;
  onOpenChannel: (channelKey: string) => void;
}

const ChannelTable = ({ config, dataset, onOpenChannel }: ChannelTableProps) => {
  const fencepostWindow = useMemo(
    () => ({
      fencepostStart: config.fencepostStart,
      fencepostEnd: config.fencepostEnd,
      reviewGranularity: dataset.reviewGranularity
    }),
    [config.fencepostStart, config.fencepostEnd, dataset.reviewGranularity]
  );
  const rows = useMemo(
    () =>
      buildChannelSummaryRows(
        config.channels,
        dataset.weeks,
        dataset.channelStats,
        fencepostWindow
      ),
    [config.channels, dataset.weeks, dataset.channelStats, fencepostWindow]
  );
  const views = useMemo(
    () => buildChannelViews(config, dataset, fencepostWindow),
    [config, dataset, fencepostWindow]
  );

  if (rows.length === 0) {
    return <div className='data-qa-about__empty'>No channels detected.</div>;
  }

  return (
    <table className='data-qa-about__table'>
      <thead>
        <tr>
          <th>Channel</th>
          <th>Group</th>
          <th className='data-qa-about__num'>Spend</th>
          <th className='data-qa-about__num'>vs. BPM</th>
          <th className='data-qa-about__num'>Impressions</th>
          <th className='data-qa-about__num'>vs. BPM</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <ChannelRows
            key={row.key}
            row={row}
            view={views.get(row.key)}
            onOpen={() => onOpenChannel(row.key)}
          />
        ))}
      </tbody>
    </table>
  );
};

interface ChannelRowsProps {
  row: ChannelSummaryRow;
  view?: ChannelView;
  onOpen: () => void;
}

// A channel's row and, once expanded, one row per platform under it. Only a channel with
// more than one platform expands -- a single platform's row would repeat the channel's.
const ChannelRows = ({ row, view, onOpen }: ChannelRowsProps) => {
  const [expanded, setExpanded] = useState(false);
  const expandable = row.platforms.length > 1;
  const reasons = view?.reasons ?? [];

  return (
    <>
      <InteractiveTr
        className='data-qa-about__row'
        aria-label={`Open ${row.label} in Media Inputs`}
        onClick={onOpen}
      >
        <td>
          <span className='data-qa-about__channel'>
            {expandable ? (
              <IconButton
                size={Size.SM}
                appearance={Appearance.EMPTY}
                className={cn('data-qa-about__expand', {
                  'data-qa-about__expand--open': expanded
                })}
                aria-expanded={expanded}
                aria-label={
                  expanded ? `Hide ${row.label} platforms` : `Show ${row.label} platforms`
                }
                // The row opens Media Inputs; this only toggles the platforms under it.
                onClick={e => {
                  e.stopPropagation();
                  setExpanded(x => !x);
                }}
                onKeyDown={e => e.stopPropagation()}
              >
                <MdChevronRight />
              </IconButton>
            ) : (
              <span className='data-qa-about__expand-spacer' />
            )}
            <IndicatorDot indicator={reasonsIndicator(reasons)} reasons={reasonTexts(reasons)} />
            {row.label}
            {expandable && (
              <span className='data-qa-about__muted'>{row.platforms.length} platforms</span>
            )}
          </span>
        </td>
        <td>
          <span
            className={cn(
              'data-qa-about__group',
              `data-qa-about__group--${GROUP_SHORT_LABEL[row.group].toLowerCase()}`
            )}
          >
            {GROUP_SHORT_LABEL[row.group]}
          </span>
        </td>
        <DeliveryCells row={row} />
      </InteractiveTr>
      {expanded &&
        row.platforms.map(platform => (
          <PlatformRow key={platform.key} platform={platform} onOpen={onOpen} />
        ))}
    </>
  );
};

interface PlatformRowProps {
  platform: PlatformSummaryRow;
  onOpen: () => void;
}

const PlatformRow = ({ platform, onOpen }: PlatformRowProps) => (
  <InteractiveTr
    className='data-qa-about__row data-qa-about__row--platform'
    aria-label={`Open ${platform.label} in Media Inputs`}
    onClick={onOpen}
  >
    {/* Across Group too: a platform is on its channel's side. */}
    <td colSpan={2}>
      <span className='data-qa-about__channel data-qa-about__platform'>
        <IndicatorDot
          indicator={reasonsIndicator(platform.reasons)}
          reasons={reasonTexts(platform.reasons)}
        />
        {platform.label}
      </span>
    </td>
    <DeliveryCells row={platform} />
  </InteractiveTr>
);

interface DeliveryCellsProps {
  row: Pick<ChannelSummaryRow, 'spend' | 'impressions' | 'spendVsBpm' | 'impressionsVsBpm'>;
}

const DeliveryCells = ({ row }: DeliveryCellsProps) => (
  <>
    <td className='data-qa-about__num'>{currencyFormatter.format(row.spend)}</td>
    <td className='data-qa-about__num'>
      <VarianceChip comparison={row.spendVsBpm} />
    </td>
    <td className='data-qa-about__num'>{integerFormatter.format(row.impressions)}</td>
    <td className='data-qa-about__num'>
      <VarianceChip comparison={row.impressionsVsBpm} />
    </td>
  </>
);

const VarianceChip = ({ comparison }: { comparison?: BpmComparison }) => {
  if (!comparison) {
    return (
      <span className='data-qa-about__no-bpm' title='No BPM data to compare against'>
        —
      </span>
    );
  }
  return (
    <span
      className={cn(
        'data-qa-about__variance',
        `data-qa-about__variance--${comparison.severity ?? 'ok'}`
      )}
    >
      {percentFormatter.format(comparison.diff)}
    </span>
  );
};

interface DemandGroupsProps {
  config: MmmConfig;
  onOpenFeature: (focus: FeatureMappingFocus) => void;
}

const DEMAND_GROUPS = [
  { key: 'demandGeneration', label: 'Demand Generation' },
  { key: 'demandCapture', label: 'Demand Capture' }
] as const;

const DemandGroups = ({ config, onOpenFeature }: DemandGroupsProps) => (
  <div className='data-qa-about__groups'>
    {DEMAND_GROUPS.map(group => (
      <DemandGroupList
        key={group.key}
        label={group.label}
        rows={buildFeatureSummaryRows(group.key, config.featureMapping, config.unmappedFeatures)}
        onOpen={feature => onOpenFeature({ group: group.key, feature })}
      />
    ))}
  </div>
);

interface DemandGroupListProps {
  label: string;
  rows: FeatureSummaryRow[];
  onOpen: (feature: string) => void;
}

const DemandGroupList = ({ label, rows, onOpen }: DemandGroupListProps) => (
  <div className='data-qa-about__group-list'>
    <h3 className='data-qa-about__group-title'>
      {label} ({rows.length})
    </h3>
    {rows.length === 0 ? (
      <div className='data-qa-about__empty'>No features in this group.</div>
    ) : (
      <ol>
        {rows.map(row => (
          <li key={row.feature || row.featureId}>
            {row.unmapped ? (
              <span>
                {row.feature || row.featureId}{' '}
                <span className='data-qa-about__tag'>No mapping data</span>
              </span>
            ) : (
              <InteractiveSpan className='data-qa-about__link' onClick={() => onOpen(row.feature)}>
                {row.feature}
              </InteractiveSpan>
            )}
          </li>
        ))}
      </ol>
    )}
  </div>
);

interface OutcomeTableProps {
  models: ModelOutcomeRow[];
  onSelectModel: (configPath: string) => void;
}

const OutcomeTable = ({ models, onSelectModel }: OutcomeTableProps) => (
  <table className='data-qa-about__table'>
    <thead>
      <tr>
        <th>Model</th>
        <th>Target metric</th>
        <th>Modeling Range</th>
        <th className='data-qa-about__num'>Metric total</th>
        <th>Review</th>
      </tr>
    </thead>
    <tbody>
      {models.map(model => (
        <InteractiveTr
          key={model.configPath}
          className={cn('data-qa-about__row', { 'data-qa-about__row--active': model.active })}
          aria-label={model.active ? 'Current model' : 'Switch to this model'}
          disabled={model.active}
          onClick={() => onSelectModel(model.configPath)}
        >
          <td>
            <span className='data-qa-about__model'>
              <ModelCapsule
                modelType={modelTypeForProductType(model.productType)}
                label={model.label}
                active={model.active}
              />
              {model.active && <span className='data-qa-about__tag'>Viewing</span>}
            </span>
          </td>
          {/* One line per stage (Brand Equity has two), lined up across these columns. */}
          <td>
            {model.targets.map(target => (
              <div key={target.label}>{target.label}</div>
            ))}
          </td>
          <td>
            {model.targets.map(target => (
              <div key={target.label}>
                <TargetWindow target={target} />
              </div>
            ))}
          </td>
          <td className='data-qa-about__num'>
            {model.targets.map(target => (
              <div key={target.label}>
                <TargetValue target={target} />
              </div>
            ))}
          </td>
          <td>
            <ReviewProgressLabel progress={model.progress} />
          </td>
        </InteractiveTr>
      ))}
    </tbody>
  </table>
);

const TargetWindow = ({ target }: { target: ModelTarget }) => {
  if (target.value === undefined) {
    return <span className='data-qa-about__muted'>Loading…</span>;
  }
  if (!target.windowStart || !target.windowEnd) {
    return <span className='data-qa-about__muted'>—</span>;
  }
  return (
    <>
      {target.windowStart} - {target.windowEnd}
    </>
  );
};

const TargetValue = ({ target }: { target: ModelTarget }) => {
  if (target.value === undefined) {
    return <span className='data-qa-about__muted'>Loading…</span>;
  }
  const formatter = target.currency ? currencyFormatter : integerFormatter;
  return target.aggregation === 'average' ? (
    <>
      {(target.currency ? currencyFormatter : averageFormatter).format(target.value)}{' '}
      <span className='data-qa-about__muted'>avg</span>
    </>
  ) : (
    <>{formatter.format(target.value)}</>
  );
};

const ReviewProgressLabel = ({ progress }: { progress?: ModelReviewProgress }) => {
  if (!progress) {
    return <span className='data-qa-about__muted'>Loading…</span>;
  }
  const { approved, flagged, total } = progress;
  if (approved === total) {
    return <span className='data-qa-about__review data-qa-about__review--done'>All approved</span>;
  }
  if (flagged > 0) {
    return (
      <span className='data-qa-about__review data-qa-about__review--flagged'>
        {flagged} flagged · {approved} of {total} approved
      </span>
    );
  }
  if (approved === 0) {
    return <span className='data-qa-about__review'>Not reviewed</span>;
  }
  return (
    <span className='data-qa-about__review'>
      {approved} of {total} approved
    </span>
  );
};
